import { AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import { fmtCislo, fmtDate } from '../receptura/receptUtils';
import { MARZE_BARVY, fmtPocet } from '../receptura/prehledGrid';
import { RozbalitVse } from '../receptura/RecipeListPanel';
import { GRID_MAT, zmenaCeny } from './materialGrid';

/**
 * Buňky sloupcové mřížky přehledu surovin. Řádek položky i řádek kategorie
 * kreslí tytéž buňky ve stejných šířkách (GRID_MAT) — kategorie jen souhrn
 * za podstrom a tlumený vzhled, aby se odlišila od konkrétní suroviny.
 *
 * Barvu nesou jen dva stavy jako v receptáři (MARZE_BARVY): zdražení / chybějící
 * cena červeně, zlevnění zeleně. Alergeny samy o sobě problém nejsou, proto
 * jsou neutrální; nevyplněné alergeny mají jen ikonu upozornění.
 */

const chipStyl = (isDark, barva = null) => ({
    background: barva ? `${barva}${isDark ? '2e' : '1f'}` : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
    color: barva || 'var(--text-primary)',
});

// ── Alergeny ─────────────────────────────────────────────────────────────────
// Sloupec roste s oknem (základ 0, ne `auto`) — se základem podle obsahu by
// začínal na každém řádku jinde podle toho, kolik čipů má.
// Položka: čísla EU alergenů (název v tooltipu), „bez alergenů", nebo
// upozornění, že je nikdo nevyplnil. Kategorie: které alergeny v ní jsou
// a kolik surovin má alergeny nevyplněné.
export function AlergenyBunka({ alergeny = [], bezAlergenu = false, nevyplneno = 0, souhrn = false, mutedColor, isDark }) {
    const serazene = [...alergeny].sort((a, b) => (a.cislo_eu ?? 99) - (b.cislo_eu ?? 99));
    const MAX = 7;

    return (
        <div className="flex items-center flex-wrap" style={{ flex: '1 1 0', minWidth: GRID_MAT.alergeny, gap: 3 }}>
            {serazene.slice(0, MAX).map(a => (
                <span key={a.id} title={a.nazev}
                    className="inline-flex items-center justify-center rounded-full text-[10px] font-bold tabular-nums"
                    style={{
                        minWidth: 18, height: 18, padding: '0 4px',
                        ...chipStyl(isDark),
                        ...(souhrn ? { color: mutedColor, fontWeight: 600 } : null),
                    }}>
                    {a.cislo_eu}
                </span>
            ))}
            {serazene.length > MAX && (
                <span className="text-[10px] font-semibold" style={{ color: mutedColor }}
                    title={serazene.slice(MAX).map(a => `${a.cislo_eu} ${a.nazev}`).join('\n')}>+{serazene.length - MAX}</span>
            )}
            {!souhrn && !serazene.length && bezAlergenu && (
                <span className="inline-flex items-center text-[10px] font-medium rounded-full"
                    style={{ padding: '2px 7px', ...chipStyl(isDark, MARZE_BARVY.dobra) }}
                    title="Zkontrolováno — surovina neobsahuje žádné alergeny">bez alergenů</span>
            )}
            {((!souhrn && !serazene.length && !bezAlergenu) || (souhrn && nevyplneno > 0)) && (
                <span className="inline-flex items-center text-[10px] font-medium rounded-full"
                    style={{ gap: 3, padding: '2px 7px', ...chipStyl(isDark), color: mutedColor }}
                    title={souhrn ? `Alergeny nevyplněny u ${nevyplneno} surovin` : 'Alergeny zatím nikdo nevyplnil'}>
                    <AlertTriangle size={10} />
                    {souhrn ? `${nevyplneno} nevyplněno` : 'nevyplněno'}
                </span>
            )}
        </div>
    );
}

// ── Dodavatel ────────────────────────────────────────────────────────────────
export function DodavatelBunka({ nazev = null, pocet = 0, souhrn = false, mutedColor }) {
    if (souhrn) {
        return (
            <div className="text-[11px] truncate" style={{ width: GRID_MAT.dodavatel, flexShrink: 0, color: mutedColor }}>
                {pocet ? `${pocet} ${pocet === 1 ? 'dodavatel' : (pocet <= 4 ? 'dodavatelé' : 'dodavatelů')}` : '—'}
            </div>
        );
    }
    const dalsi = Math.max(0, pocet - (nazev ? 1 : 0));
    return (
        <div className="flex items-center min-w-0" style={{ width: GRID_MAT.dodavatel, flexShrink: 0, gap: 6 }}
            title={dalsi ? `Aktuální cena od: ${nazev || '—'} · další dodavatelé v ceníku: ${dalsi}` : undefined}>
            <span className="text-[12px] truncate" style={{ color: nazev ? 'var(--text-primary)' : mutedColor }}>{nazev || '—'}</span>
            {dalsi > 0 && <span className="text-[10px] font-semibold tabular-nums" style={{ color: mutedColor, flexShrink: 0 }}>+{dalsi}</span>}
        </div>
    );
}

// ── Cena za jednotku + změna ─────────────────────────────────────────────────
// Změna je proti předchozí ceně téhož dodavatele. Zdražení = červeně (náklad
// receptur roste), zlevnění = zeleně. Kategorie: MEDIÁN změn — průměr by
// utáhla jediná položka s chybou v ceníku (jednotky: +1 400 %).
export function CenaBunka({ polozka = null, jednotka = null, medianZmeny = null, pocetZmen = 0, souhrn = false, mutedColor }) {
    const Zmena = ({ pct, popis }) => {
        if (pct == null || Math.abs(pct) < 0.5) return null;
        const nahoru = pct > 0;
        const Ikona = nahoru ? TrendingUp : TrendingDown;
        const barva = nahoru ? MARZE_BARVY.kriticka : MARZE_BARVY.dobra;
        return (
            <span className="inline-flex items-center justify-end text-[10px] font-semibold tabular-nums" style={{ gap: 3, color: barva }} title={popis}>
                <Ikona size={11} />{nahoru ? '+' : '−'}{Math.abs(pct).toFixed(1).replace('.', ',')} %
            </span>
        );
    };

    if (souhrn) {
        return (
            <div style={{ width: GRID_MAT.cena, flexShrink: 0, textAlign: 'right' }}>
                {medianZmeny != null && Math.abs(medianZmeny) >= 0.5
                    ? <span className="text-[11px]" style={{ color: mutedColor }}>med. <Zmena pct={medianZmeny} popis={`Medián poslední změny ceny u ${pocetZmen} surovin`} /></span>
                    : <span className="text-[11px]" style={{ color: mutedColor }}>—</span>}
            </div>
        );
    }

    const cena = polozka?.aktualni_cena;
    const pct = zmenaCeny(polozka);
    return (
        <div style={{ width: GRID_MAT.cena, flexShrink: 0, textAlign: 'right' }}>
            {cena != null ? (
                <div className="text-[12px] tabular-nums" style={{ fontFamily: 'ui-monospace, monospace' }}>
                    <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{fmtCislo(cena)}</span>
                    <span className="text-[10px]" style={{ color: mutedColor }}> Kč{jednotka ? `/${jednotka}` : ''}</span>
                </div>
            ) : (
                <div className="inline-flex items-center justify-end text-[11px] font-semibold" style={{ gap: 3, color: MARZE_BARVY.kriticka }}
                    title="Bez platné ceny — do kalkulace receptur vstupuje nulou">
                    <AlertTriangle size={11} /> chybí cena
                </div>
            )}
            {pct != null && (
                <div style={{ marginTop: 1 }}>
                    <Zmena pct={pct} popis={`Předchozí cena ${fmtCislo(polozka.predchozi_cena)} Kč${jednotka ? `/${jednotka}` : ''}`} />
                </div>
            )}
        </div>
    );
}

// ── Od kdy cena platí ────────────────────────────────────────────────────────
export function CenaOdBunka({ od = null, mutedColor }) {
    return (
        <div className="text-[11px] tabular-nums text-right" style={{ width: GRID_MAT.cenaOd, flexShrink: 0, color: mutedColor, fontFamily: 'ui-monospace, monospace' }}
            title={od ? `Aktuální cena platí od ${fmtDate(od)}` : undefined}>
            {od ? fmtDate(od) : '—'}
        </div>
    );
}

// ── Použití v kusovnících ────────────────────────────────────────────────────
export function PouzitoBunka({ pocet = 0, souhrn = false, mutedColor }) {
    return (
        <div className="text-[11px] tabular-nums text-right"
            style={{ width: GRID_MAT.pouzito, flexShrink: 0, color: pocet ? 'var(--text-primary)' : mutedColor, fontWeight: souhrn && pocet ? 600 : 400 }}
            title={pocet ? `V recepturách ${fmtPocet(pocet)}×` : 'Zatím v žádné receptuře'}>
            {`${fmtPocet(pocet || 0)}×`}
        </div>
    );
}

/**
 * Hlavička mřížky — popisky jednou nad seznamem. Šířky z GRID_MAT, takže
 * sedí nad hodnotami položek i kategorií.
 */
export function HlavickaMrizkyMaterialu({ isDark, sloupce, rozbaleni }) {
    const l = 'text-[10px] font-semibold uppercase tracking-wider truncate';
    const barva = isDark ? 'rgba(255,255,255,0.62)' : 'var(--text-secondary)';
    const bunka = (label, sirka, zarovnani = 'left') => (
        <div className={l} style={{ width: sirka, flexShrink: 0, textAlign: zarovnani }}>{label}</div>
    );

    return (
        <div className="flex items-end"
            style={{
                gap: GRID_MAT.mezera, padding: '7px 10px 6px 13px', color: barva,
                background: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.022)',
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
            }}>
            <div className="flex items-center justify-center" style={{ width: GRID_MAT.uchyt, flexShrink: 0, alignSelf: 'center' }}>
                <RozbalitVse rozbaleni={rozbaleni} mutedColor={barva} />
            </div>
            {sloupce?.nahled !== false && <div style={{ width: GRID_MAT.nahled, flexShrink: 0 }} />}
            <div className={l} style={{ flex: `${GRID_MAT.polozkaRust} 1 0`, minWidth: GRID_MAT.polozkaMin }}>surovina</div>
            <div className={l} style={{ flex: '1 1 0', minWidth: GRID_MAT.alergeny }}>alergeny</div>
            {sloupce?.dodavatel !== false && bunka('dodavatel', GRID_MAT.dodavatel)}
            {bunka('cena bez DPH', GRID_MAT.cena, 'right')}
            {sloupce?.cenaOd !== false && bunka('cena od', GRID_MAT.cenaOd, 'right')}
            {bunka('použito', GRID_MAT.pouzito, 'right')}
            {bunka('stav', GRID_MAT.stav, 'right')}
        </div>
    );
}
